import { useState } from "react";
import { Loader2, Star, GraduationCap } from "lucide-react";
import { toast } from "sonner";
import { http, formatApiError } from "@/lib/api";
import { useProfile } from "@/context/ProfileContext";
import { speak } from "@/lib/buddy";
import { BuddyAvatar } from "@/components/buddy/BuddyAvatar";
import { VoiceInput } from "@/components/buddy/VoiceInput";

const Reply = ({ r, buddy }) => (
  <div className="rounded-3xl bg-[#E3EBE5] p-6 rise" data-testid="teach-reply">
    <div className="flex items-center gap-3">
      <BuddyAvatar buddy={buddy} className="w-12 h-12" testId="teach-reply-avatar" />
      <div className="flex gap-0.5" data-testid="teach-stars">{[1, 2, 3].map((n) => <Star key={n} className={`w-5 h-5 ${n <= r.stars ? "fill-[#D99B26] text-[#D99B26]" : "text-stone-300"}`} />)}</div>
    </div>
    <p className="font-display text-lg text-[#2C4A3E] mt-4">{r.reply}</p>
    <p className="text-sm text-stone-700 mt-2">{r.feedback}</p>
    {r.question && <p className="mt-4 font-display italic text-[#C87D53]" data-testid="teach-question">{buddy.name} asks: "{r.question}"</p>}
  </div>
);

export const TeachLesson = ({ topics, onResult }) => {
  const { buddy } = useProfile();
  const [topic, setTopic] = useState(topics[0] || null);
  const [custom, setCustom] = useState("");
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [thread, setThread] = useState([]);
  const last = thread[thread.length - 1];
  const name = topic?.topic || custom;

  const teach = async () => {
    if (!name.trim() || text.trim().length < 3) return toast.error("Pick a topic and tell your buddy what you learned");
    setBusy(true);
    try {
      const r = await http.post("/buddy/teach/lesson", { topic: name, explanation: text, subject_id: topic?.subject_id || null, buddy_question: last?.question || null });
      setThread((t) => [...t, r.data]); setText(""); onResult(r.data);
      speak(`${r.data.reply} ${r.data.question}`, buddy.voice).catch(() => {});
      r.data.new_badges.forEach((b) => toast.success(`New badge: ${b}`));
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };
  const pick = (t) => { setTopic(t); setThread([]); };

  return (
    <div className="grid lg:grid-cols-2 gap-8" data-testid="teach-lesson">
      <div className="rounded-3xl bg-[#F7F3EB] border border-[#E6DEC3] p-6">
        <p className="font-display text-2xl text-[#2C4A3E] inline-flex items-center gap-2"><GraduationCap className="w-6 h-6 text-[#C87D53]" /> You're the teacher!</p>
        <p className="text-sm text-stone-500 mt-1">Pick something you learned and explain it to {buddy.name}.</p>
        <div className="flex flex-wrap gap-2 mt-4">
          {topics.map((t, i) => (
            <button key={i} onClick={() => pick(t)} data-testid={`teach-topic-${i}`}
              className={`px-3.5 py-1.5 rounded-full text-sm border transition-colors ${topic === t ? "bg-[#2C4A3E] text-white border-[#2C4A3E]" : "bg-white border-[#E6DEC3]"}`}>{t.topic}</button>
          ))}
          <input value={custom} onFocus={() => pick(null)} onChange={(e) => setCustom(e.target.value)} maxLength={140} placeholder="Or type a topic…" data-testid="teach-custom-topic"
            className={`px-3.5 py-1.5 rounded-full text-sm border bg-white ${!topic ? "border-[#2C4A3E]" : "border-[#E6DEC3]"}`} />
        </div>
        <textarea value={text} onChange={(e) => setText(e.target.value)} rows={4} maxLength={2000} data-testid="teach-explanation-input"
          placeholder={last?.question ? `Answer ${buddy.name}'s question…` : `Tell ${buddy.name} what you learned…`}
          className="w-full mt-5 px-4 py-3 rounded-2xl bg-white border border-[#E6DEC3] focus:outline-none focus:border-[#2C4A3E]" />
        <div className="flex items-center gap-3 mt-3">
          <VoiceInput onText={(t) => setText((x) => (x ? `${x} ${t}` : t))} />
          <span className="text-xs text-stone-500">Tap the mic and say it out loud</span>
          <button onClick={teach} disabled={busy} data-testid="teach-submit-btn"
            className="ml-auto inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-[#2C4A3E] text-white disabled:opacity-50 hover:bg-[#1E352B]">
            {busy && <Loader2 className="w-4 h-4 animate-spin" />} {last?.question ? "Answer" : `Teach ${buddy.name}`}
          </button>
        </div>
      </div>
      <div className="space-y-4" data-testid="teach-thread">
        {thread.map((r) => <Reply key={r.id} r={r} buddy={buddy} />)}
        {!thread.length && <div className="h-full min-h-[200px] rounded-3xl border-2 border-dashed border-[#E6DEC3] grid place-items-center text-stone-400 text-sm p-6 text-center">{buddy.name} is listening and ready to learn from you.</div>}
      </div>
    </div>
  );
};
